import type { ReactNode } from 'react';

import { cn } from '@/lib/cn';

export interface Column<Row> {
  /** Stable key. Also used for the React key on cells. */
  key: string;
  header: ReactNode;
  /** The cell. Keep it presentational - no data fetching in here. */
  cell: (row: Row) => ReactNode;
  align?: 'left' | 'right';
  /** Numbers get mono + tabular figures so columns do not jitter. */
  numeric?: boolean;
  /** Hidden below `sm`. Use for anything that is not the story of the row. */
  secondary?: boolean;
  width?: string;
}

export interface TableProps<Row> {
  columns: readonly Column<Row>[];
  rows: readonly Row[];
  rowKey: (row: Row) => string;
  /** Rendered instead of the body when `rows` is empty. */
  empty?: ReactNode;
  onRowClick?: (row: Row) => void;
  /** Extra classes on a row - a blocked hatch, a muted rejected entry. */
  rowClassName?: (row: Row) => string | undefined;
  caption?: string;
  className?: string;
}

/**
 * A data table that survives a phone.
 *
 * Two mechanisms, used together: columns marked `secondary` are hidden below
 * the `sm` breakpoint, and whatever is left scrolls horizontally *inside the
 * table's own box*. The page itself never scrolls sideways, which is the rule
 * in theme.css.
 */
export function Table<Row>({
  columns,
  rows,
  rowKey,
  empty,
  onRowClick,
  rowClassName,
  caption,
  className,
}: TableProps<Row>) {
  if (rows.length === 0 && empty) {
    return <div className={className}>{empty}</div>;
  }

  return (
    <div className={cn('scroll-x rounded-lg border border-line bg-surface', className)}>
      <table className="w-full border-collapse text-ui">
        {caption ? <caption className="sr-only">{caption}</caption> : null}
        <thead>
          <tr className="border-b border-line bg-paper/60">
            {columns.map((column) => (
              <th
                key={column.key}
                scope="col"
                style={column.width ? { width: column.width } : undefined}
                className={cn(
                  'whitespace-nowrap px-3 py-2.5 text-nano font-semibold uppercase tracking-[0.1em] text-subtle',
                  column.align === 'right' ? 'text-right' : 'text-left',
                  column.secondary && 'hidden sm:table-cell',
                )}
              >
                {column.header}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {rows.map((row) => (
            <tr
              key={rowKey(row)}
              onClick={onRowClick ? () => onRowClick(row) : undefined}
              className={cn(
                'border-b border-line last:border-b-0',
                onRowClick && 'cursor-pointer transition-colors hover:bg-sunken',
                rowClassName?.(row),
              )}
            >
              {columns.map((column) => (
                <td
                  key={column.key}
                  className={cn(
                    'px-3 py-2.5 align-middle',
                    column.align === 'right' ? 'text-right' : 'text-left',
                    column.numeric && 'font-mono text-small',
                    column.secondary && 'hidden sm:table-cell',
                  )}
                >
                  {column.cell(row)}
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
